import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import { getIspInvoicePdfLink } from '@/api/isp';

/**
 * One bill, as the CUSTOMER sees it — openable and printable from the portal.
 *
 * Ray, 2026-09-01, of a row that just said "INV-1001 · Paid": *"how is this
 * useful? it has no download or print or ready to pay qr code or open
 * invoice?"* He is right. A summary row answers nothing anybody would actually
 * ask: what was I charged for, what did I pay, and can I have a copy.
 *
 * ─── PRINTS THE SAME SHEET IT SHOWS ───
 * The print CSS hides everything and un-hides this, then FLATTENS every
 * ancestor — a `transform` or `position: fixed` anywhere above makes an
 * absolutely positioned descendant lay itself out against the wrong box, which
 * is exactly how the operator's invoice printed blank.
 */
const peso = (n: number) =>
   `₱${Number(n ?? 0).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
   })}`;

const day = (iso: string) =>
   new Date(iso).toLocaleDateString('en-PH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
   });

interface Props {
   brand: {
      businessName: string;
      logoUrl: string;
      address: string;
      contactNumber: string;
   };
   customer: { fullName: string; customerCode: string };
   invoice: {
      /** Needed to ask the server for this bill's PDF. */
      id?: string;
      number: string;
      periodStart: string;
      periodEnd: string;
      dueAt: string;
      total: number;
      paidAmount: number;
      outstanding: number;
      status: string;
      paidAt: string | null;
      lines: { planName: string; amount: number }[];
   };
   methods: {
      id: string;
      kind: string;
      accountName: string;
      accountNumber: string;
      qrUrl: string;
      instructions: string;
   }[];
   onClose: () => void;
   /** The portal token, used to ask for a download link for THIS bill. */
   token?: string;
}

const IspBillSheet: React.FC<Props> = ({
   brand,
   customer,
   invoice,
   methods,
   onClose,
   token,
}) => {
   const [hint, setHint] = useState('');
   const [busy, setBusy] = useState(false);

   useEffect(() => {
      const sheet = document.getElementById('isp-bill-sheet');
      const marked: HTMLElement[] = [];
      for (
         let p = sheet?.parentElement;
         p && p !== document.body;
         p = p.parentElement
      ) {
         p.classList.add('isp-print-ancestor');
         marked.push(p);
      }
      return () =>
         marked.forEach((el) => el.classList.remove('isp-print-ancestor'));
      // Runs once the sheet is in the DOM. An empty dep array is right: the
      // sheet is mounted and unmounted with this component.
   }, []);

   /**
    * Save the bill as a real PDF.
    *
    * ─── NO window.print() ANYWHERE NEAR THIS ───
    * Ray, twice: *"the save pdf still nothing happens."* Printing from a phone
    * is not something a page can rely on — an in-app browser has no print
    * dialog, and calling it there does nothing and reports nothing. He settled
    * it: *"just save pdf no need to print if the portal is opened in phone."*
    *
    * 🔴 SO THE PAGE NAVIGATES TO A FILE. The server answers with
    * application/pdf and the phone opens its own viewer, which already has
    * saving and sharing. Nothing depends on the browser cooperating, and there
    * is no blob or download for a webview to block.
    */
   const savePdf = async () => {
      if (!token || !invoice.id) {
         setHint('Open this bill again to download it.');
         return;
      }
      setBusy(true);
      setHint('');
      try {
         const url = await getIspInvoicePdfLink(token, invoice.id);
         window.location.href = url;
      } catch {
         setHint('Could not prepare the file. Try again in a moment.');
      } finally {
         setBusy(false);
      }
   };

   const unpaid = invoice.outstanding > 0;

   return (
      <Overlay>
         <PrintRules />
         <Panel>
            <Grabber className="isp-print-hide" />
            <Sheet id="isp-bill-sheet">
               <Head>
                  <div>
                     {brand.logoUrl ? <Logo src={brand.logoUrl} alt="" /> : null}
                     <Biz>{brand.businessName}</Biz>
                     {brand.address ? <Line>{brand.address}</Line> : null}
                     {brand.contactNumber ? (
                        <Line>{brand.contactNumber}</Line>
                     ) : null}
                  </div>
                  <HeadRight>
                     <DocTitle>{unpaid ? 'INVOICE' : 'RECEIPT'}</DocTitle>
                     <Line>
                        <strong>{invoice.number}</strong>
                     </Line>
                     <Line>
                        {day(invoice.periodStart)} — {day(invoice.periodEnd)}
                     </Line>
                  </HeadRight>
               </Head>

               <Parties>
                  <div>
                     <PartyLbl>Billed to</PartyLbl>
                     <PartyName>{customer.fullName}</PartyName>
                     <Line>Account {customer.customerCode}</Line>
                  </div>
                  <div>
                     <PartyLbl>Due</PartyLbl>
                     <PartyName>{day(invoice.dueAt)}</PartyName>
                     {invoice.paidAt ? (
                        <Line>Paid {day(invoice.paidAt)}</Line>
                     ) : null}
                  </div>
               </Parties>

               <Table>
                  <thead>
                     <tr>
                        <Th>Connection</Th>
                        <Th $right>Amount</Th>
                     </tr>
                  </thead>
                  <tbody>
                     {invoice.lines.map((l, i) => (
                        <tr key={i}>
                           <Td>{l.planName}</Td>
                           <Td $right>{peso(l.amount)}</Td>
                        </tr>
                     ))}
                  </tbody>
               </Table>

               <Totals>
                  <TRow>
                     <span>Total</span>
                     <span>{peso(invoice.total)}</span>
                  </TRow>
                  {invoice.paidAmount > 0 && (
                     <TRow>
                        <span>Paid</span>
                        <span>−{peso(invoice.paidAmount)}</span>
                     </TRow>
                  )}
                  {/* 🔴 What is still OWED, not the total. A part-paid bill
                      showing only its total tells somebody to pay twice. */}
                  <TRow $big>
                     <span>{unpaid ? 'Amount due' : 'Paid in full'}</span>
                     <span>{unpaid ? peso(invoice.outstanding) : peso(0)}</span>
                  </TRow>
               </Totals>

               {/* Payment details on a RECEIPT invite a second payment. */}
               {unpaid && methods.length > 0 && (
                  <Pay>
                     <PayLbl>How to pay</PayLbl>
                     {methods.map((m) => (
                        <PayRow key={m.id}>
                           <div>
                              <strong>{m.accountName}</strong>
                              {m.accountNumber ? (
                                 <Line>{m.accountNumber}</Line>
                              ) : null}
                              {m.instructions ? (
                                 <Line>{m.instructions}</Line>
                              ) : null}
                           </div>
                           {m.qrUrl ? <Qr src={m.qrUrl} alt="" /> : null}
                        </PayRow>
                     ))}
                  </Pay>
               )}

               <Foot>
                  Questions about this bill? Call{' '}
                  {brand.contactNumber || 'your provider'}.
               </Foot>
            </Sheet>

            {hint ? <Hint className="isp-print-hide">{hint}</Hint> : null}
            <Actions className="isp-print-hide">
               <Ghost type="button" onClick={onClose}>
                  Close
               </Ghost>
               <Primary type="button" disabled={busy} onClick={savePdf}>
                  {busy ? 'Preparing…' : 'Save PDF'}
               </Primary>
            </Actions>
         </Panel>
      </Overlay>
   );
};

const PrintRules = createGlobalStyle`
   @media print {
      body * { visibility: hidden !important; }
      #isp-bill-sheet, #isp-bill-sheet * { visibility: visible !important; }
      .isp-print-ancestor {
         position: static !important;
         transform: none !important;
         overflow: visible !important;
         inset: auto !important;
         width: auto !important;
         max-width: none !important;
         height: auto !important;
         max-height: none !important;
         margin: 0 !important;
         padding: 0 !important;
         border: none !important;
         border-radius: 0 !important;
         box-shadow: none !important;
         background: #fff !important;
         display: block !important;
      }
      #isp-bill-sheet {
         position: static;
         width: 100%;
         padding: 0;
         border: none;
         color: #000;
         background: #fff;
      }
      .isp-print-hide { display: none !important; }
      /* 🔴 THE OVERLAY ITSELF MUST NOT CLIP. It is position:fixed with
         overflow:auto, so on paper it becomes a one-page window over a
         document taller than itself — the sheet is cut off, or lands
         nowhere at all. Ray, 2026-09-02: "when i click the print save pdf,
         nothing happens." */
      html, body {
         height: auto !important;
         overflow: visible !important;
      }
      @page { margin: 14mm; }
   }
`;

/**
 * A centred dialog on a desktop, a SHEET THAT SLIDES UP on a phone.
 *
 * Ray, 2026-09-03: *"the open modal invoice too big buttons, not really
 * compact content, i think if its mobile view then it should use popup slide
 * up not a modal."* He is right about the shape: a full bill floated in the
 * middle of a phone screen has nowhere to go, while a sheet anchored to the
 * bottom is the pattern every wallet app uses and reaches the thumb.
 */
const Overlay = styled.div`
   position: fixed;
   inset: 0;
   z-index: 1600;
   background: rgba(17, 24, 39, 0.6);
   overflow: auto;
   padding: 18px 14px 40px;

   @media (max-width: 640px) {
      display: flex;
      align-items: flex-end;
      padding: 0;
      overflow: hidden;
   }
`;

const riseUp = keyframes`
   from { transform: translateY(100%); }
   to   { transform: translateY(0); }
`;

const Panel = styled.div`
   max-width: 520px;
   margin: 0 auto;
   background: ${colors.WHITE};
   border-radius: 16px;
   padding: 20px;

   @media (max-width: 640px) {
      width: 100%;
      max-height: 92vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      border-radius: 20px 20px 0 0;
      padding: 8px 16px calc(16px + env(safe-area-inset-bottom));
      animation: ${riseUp} 0.24s ease-out;

      @media (prefers-reduced-motion: reduce) {
         animation: none;
      }
   }
`;

/** The little bar that says "drag me" — only on the phone sheet. */
const Grabber = styled.div`
   display: none;

   @media (max-width: 640px) {
      display: block;
      width: 42px;
      height: 4px;
      margin: 6px auto 12px;
      border-radius: 99px;
      background: ${colors.GRAY12};
   }
`;
const Sheet = styled.div`
   font-size: 13px;
   line-height: 1.6;
`;
const Head = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 18px;
   padding-bottom: 14px;
   border-bottom: 2px solid ${colors.GRAY12};
`;
const HeadRight = styled.div`
   text-align: right;
`;
/**
 * 🔴 `height: auto` IS THE FIX. With only `max-height` set, a logo wider than
 * it is tall gets squashed — the browser honours the height cap and leaves the
 * width alone. Ray, 2026-09-02: *"the logo height. it looks shrink."*
 */
const Logo = styled.img`
   max-height: 42px;
   max-width: 150px;
   width: auto;
   height: auto;
   object-fit: contain;
   border-radius: 8px;
   display: block;
   margin-bottom: 6px;
`;
const Biz = styled.div`
   font-size: 16px;
   font-weight: 800;
`;
const DocTitle = styled.div`
   font-size: 16px;
   font-weight: 800;
   letter-spacing: 1px;
`;
const Line = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Parties = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 18px;
   margin: 16px 0;
`;
const PartyLbl = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
`;
const PartyName = styled.div`
   font-weight: 700;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
`;
const Th = styled.th<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
   padding: 6px 0;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Td = styled.td<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   padding: 9px 0;
   border-bottom: 1px solid ${colors.GRAY12};
`;
const Totals = styled.div`
   margin: 14px 0 0 auto;
   max-width: 260px;
`;
const TRow = styled.div<{ $big?: boolean }>`
   display: flex;
   justify-content: space-between;
   padding: 5px 0;
   font-size: ${({ $big }) => ($big ? '15px' : '13px')};
   font-weight: ${({ $big }) => ($big ? 800 : 500)};
   border-top: ${({ $big }) => ($big ? `2px solid ${colors.GRAY12}` : 'none')};
`;
const Pay = styled.div`
   margin-top: 20px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY12};
`;
const PayLbl = styled.div`
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
   margin-bottom: 8px;
`;
const PayRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 14px;
   padding: 8px 0;
`;
const Qr = styled.img`
   width: 96px;
   height: 96px;
   object-fit: contain;
`;
const Foot = styled.div`
   margin-top: 18px;
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Hint = styled.p`
   margin: 12px 16px 0;
   font-size: 13px;
   font-weight: 600;
   line-height: 1.45;
   color: ${colors.RED};
`;
/* Compact, and stuck to the bottom of the phone sheet so it is always in
   reach without scrolling the whole bill. */
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 16px;

   @media (max-width: 640px) {
      position: sticky;
      bottom: 0;
      margin: 12px -16px 0;
      padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
      background: ${colors.WHITE};
      border-top: 1px solid ${colors.GRAY13};
   }
`;
const btn = `
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 16px;
   border-radius: 10px;
   cursor: pointer;
   white-space: nowrap;
`;
const Primary = styled.button`
   ${btn}
   font-weight: 800;
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;

   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;
const Ghost = styled.button`
   ${btn}
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
`;

export default IspBillSheet;
